import React, { useEffect } from 'react'
import { redirectTo, WINDOW_TARGET } from '@presentation/helpers'
import { useNavigate } from 'react-router-dom'
import useAccount from '@presentation/hooks/auth/use-account'
import { ValidAccountState } from '@presentation/contexts/auth'
import { useQuery, useQueryClient } from 'react-query'
import { UserProfileAssessment } from '@/domain/usecases/profile-assessment/user-profile-assessment'
import { makeRemoteUserProfileAssessment } from '@/main/factories/usecases'
import { UserProfileAssessmentModel } from '@/domain/models/profile-assessment/user-profile-assessment-model'
import { Analytics } from './analytics'
import PathConstants from '@/main/routes/path-contants'
import { Button, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { styled } from '@mui/material/styles'
import recognitaStep1 from '@images/recognita-step-1.svg'
import recognitaStep2 from '@images/recognita-step-2.svg'
import recognitaStep3 from '@images/recognita-step-3.svg'
import recognitaStep4 from '@images/recognita-step-4.svg'
import { i18nTranslationHelper } from '@/i18n'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

const StepIcon = styled('img')(({ theme }) => ({
  width: 44,
  height: 44,
  marginRight: theme.spacing(2),
  flexShrink: 0
}))

const StepBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3)
}))

const steps = [
  {
    icon: recognitaStep1,
    title: 'assessmentGetStarted.step1.title',
    description: 'assessmentGetStarted.step1.description',
    iconAltText: 'assessmentGetStarted.step1.iconAltText'
  },
  {
    icon: recognitaStep2,
    title: 'assessmentGetStarted.step2.title',
    description: 'assessmentGetStarted.step2.description',
    iconAltText: 'assessmentGetStarted.step2.iconAltText'
  },
  {
    icon: recognitaStep3,
    title: 'assessmentGetStarted.step3.title',
    description: 'assessmentGetStarted.step3.description',
    iconAltText: 'assessmentGetStarted.step3.iconAltText'
  },
  {
    icon: recognitaStep4,
    title: 'assessmentGetStarted.step4.title',
    description: 'assessmentGetStarted.step4.description',
    iconAltText: 'assessmentGetStarted.step4.iconAltText'
  }
]

const ProfileAssessmentGetStartedPage: React.FC = () => {
  const remote = makeRemoteUserProfileAssessment(useQueryClient())
  const [account, setAccount] = useAccount() as ValidAccountState
  const { latestProfileAssessment } = account
  const navigate = useNavigate()
  const t = i18nTranslationHelper.getTComponents
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { data, isFetching, refetch, remove } = useQuery({
    queryKey: UserProfileAssessment.KeyCreate,
    queryFn: () => remote.create(),
    enabled: false,
    retry: 2
  })

  const handleStartAssessment = () => {
    if (latestProfileAssessment?.url) {
      openUrl(latestProfileAssessment)
    } else {
      refetch()
    }
  }

  const openUrl = (data: UserProfileAssessmentModel) => {
    Analytics.trackOpenProfileAssessment(data)
    if (latestProfileAssessment?.finished) {
      navigate(PathConstants.Routes.USER_SPACE.value)
    } else {
      redirectTo(data.url, WINDOW_TARGET.BLANK)
      navigate(PathConstants.Routes.PROFILE_ASSESSMENT_IN_PROGRESS.value)
    }
  }

  useEffect(() => {
    if (data?.url) {
      setAccount({
        ...account,
        latestProfileAssessment: data
      })
      openUrl(data)
    }
    remove()
  }, [data])

  return (
    <Box
      sx={{
        bgcolor: theme => theme.palette.background.default,
        minHeight: theme => `calc(100vh - ${theme.spacing(8)} - ${theme.spacing(9)})`,
        py: {
          xs: theme.spacing(2),
          md: theme.spacing(6)
        },
        px: theme => (isMobile ? theme.spacing(2) : 0)
      }}
    >
      <Card
        sx={{
          maxWidth: 540,
          mx: 'auto',
          borderRadius: theme => theme.spacing(2),
          boxShadow: 0,
          px: theme => ({
            xs: theme.spacing(2),
            md: theme.spacing(4)
          }),
          pt: theme => ({
            xs: theme.spacing(2),
            md: theme.spacing(2)
          })
        }}
      >
        <CardContent>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700} gutterBottom>
            {t('assessmentGetStarted.title')}
          </Typography>
          <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary" mb={4}>
            {t('assessmentGetStarted.description')}
          </Typography>
          {steps.map((step, index) => (
            <StepBox key={index}>
              <StepIcon src={step.icon} alt={t(step.iconAltText)} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {t(step.title)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(step.description)}
                </Typography>
              </Box>
            </StepBox>
          ))}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleStartAssessment}
            disabled={isFetching}
            sx={{ mt: 2 }}
          >
            <Typography
              variant="button"
              fontSize={`1em`}
              fontWeight={700}
              sx={{ textTransform: 'none' }}
            >
              {t('assessmentGetStarted.cta')}
            </Typography>
          </Button>
          <Button
            fullWidth
            variant="text"
            color="inherit"
            sx={{ mt: 2, fontWeight: 700 }}
            onClick={() => navigate(PathConstants.Routes.HOME.value)}
          >
            <Typography
              variant="button"
              fontSize={`1em`}
              fontWeight={700}
              sx={{ textTransform: 'none' }}
            >
              {t('assessmentGetStarted.cancelCta')}
            </Typography>
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}

export default ProfileAssessmentGetStartedPage
